import React, { useEffect, useMemo, useRef, useState } from 'react';
import { verifyInvoiceDob } from '../../services/paymentsService';

const RECAPTCHA_SITE_KEY = '6LeEbOkrAAAAAJvzZzXa_3XbRJXTzvVVQ16uU813';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Days/year in a given month — defaults to 31 when the month or year
// hasn't been picked yet so the day list stays clickable.
const daysInMonth = (month, year) => {
    if (!month) return 31;
    const m = Number(month);
    const y = Number(year) || 2000; // 2000 is a leap year, safe default
    return new Date(y, m, 0).getDate();
};

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * DobGate
 * =======
 *
 * Asks the patient for their date of birth to unlock the saved-card
 * panel on the public invoice page.
 *
 * Inputs are masked into ``YYYY-MM-DD`` as the user types so that the
 * payload we send the server is always in the canonical format the
 * KollaContacts.birth_date column stores. The server does
 * constant-time comparison + per-invoice rate limiting; this component
 * is responsible for translating the structured error responses
 * (mismatch / lockout / DOB unavailable) into UX:
 *
 *   - 200          → onVerified({gateToken, firstName, hasCards})
 *   - 403 mismatch → inline "doesn't match" with attempts-remaining hint
 *   - 429 locked   → disable input, show countdown until lockedUntil
 *   - 412 DOB-unavailable → call onUnavailable() so the parent silently
 *                            falls back to the existing guest-pay flow
 *                            (no confusing "DOB required but we don't
 *                            know yours" prompt for the patient).
 *
 * reCAPTCHA v3 token is generated fresh per submission with action=
 * "verify_dob" so the backend can score it independently of the
 * "view_invoice" action used elsewhere on this page.
 */
export default function DobGate({ publicHash, onVerified, onUnavailable, onCancel, autoFocus = true }) {
    // We track month/day/year independently so the three native <select>s
    // can each act as a discrete control. The submit handler reassembles
    // them into the canonical YYYY-MM-DD shape the API expects.
    const [month, setMonth] = useState('');
    const [day, setDay] = useState('');
    const [year, setYear] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);
    const [lockedUntil, setLockedUntil] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    const monthRef = useRef(null);
    const dayRef = useRef(null);
    const yearRef = useRef(null);

    // Year window: today back 120 years. Descending so the most-recent
    // year sits at the top — adults can scroll a few rows or just type
    // the year (native <select> supports keyboard search).
    const years = useMemo(() => {
        const current = new Date().getFullYear();
        const out = [];
        for (let y = current; y >= current - 120; y -= 1) out.push(y);
        return out;
    }, []);

    const dayOptions = useMemo(() => {
        const max = daysInMonth(month, year);
        const out = [];
        for (let d = 1; d <= max; d += 1) out.push(d);
        return out;
    }, [month, year]);

    // If the user picks Feb 30 then changes year/month to make it invalid,
    // snap day back into the valid range rather than silently submitting
    // a malformed date.
    useEffect(() => {
        if (day && Number(day) > daysInMonth(month, year)) {
            setDay('');
        }
    }, [month, year, day]);

    useEffect(() => {
        if (!lockedUntil) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [lockedUntil]);

    const lockSecondsLeft = useMemo(() => {
        if (!lockedUntil) return 0;
        const target = new Date(lockedUntil).getTime();
        return Math.max(0, Math.ceil((target - now) / 1000));
    }, [lockedUntil, now]);

    // Auto-advance focus as the user fills each field so they don't
    // have to tab/click between Month → Day → Year.
    const handleMonthChange = (e) => {
        setMonth(e.target.value);
        if (error) setError(null);
        if (e.target.value && dayRef.current) dayRef.current.focus();
    };
    const handleDayChange = (e) => {
        setDay(e.target.value);
        if (error) setError(null);
        if (e.target.value && yearRef.current) yearRef.current.focus();
    };
    const handleYearChange = (e) => {
        setYear(e.target.value);
        if (error) setError(null);
    };

    const composedDob = month && day && year
        ? `${year}-${pad2(month)}-${pad2(day)}`
        : '';

    const obtainCaptchaToken = () => new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
            reject(new Error('Security verification is still loading. Please try again.'));
            return;
        }
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'verify_dob' })
                .then(resolve, reject);
        });
    });

    const handleSubmit = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (submitting || lockSecondsLeft > 0) return;

        if (!composedDob) {
            setError('Please select your month, day, and year of birth.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const captchaToken = await obtainCaptchaToken();
            const { ok, status, data } = await verifyInvoiceDob(publicHash, {
                birthDate: composedDob,
                captchaToken,
            });

            if (ok && data?.success) {
                setAttemptsRemaining(null);
                setLockedUntil(null);
                onVerified?.({
                    gateToken: data.gateToken,
                    firstName: data.firstName,
                    hasCards: !!data.hasCards,
                    savedCount: data.savedCount || 0,
                });
                return;
            }

            if (status === 412 || data?.error === 'DOB_UNAVAILABLE') {
                onUnavailable?.();
                return;
            }

            if (status === 429) {
                setLockedUntil(data?.lockedUntil || null);
                setError(data?.error || 'Too many attempts. Please try again later.');
                return;
            }

            if (typeof data?.attemptsRemaining === 'number') {
                setAttemptsRemaining(data.attemptsRemaining);
            }
            setError(data?.error || 'Verification failed. Please try again.');
        } catch (err) {
            setError(err?.message || 'Verification failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const disabled = submitting || lockSecondsLeft > 0;

    return (
        <form onSubmit={handleSubmit} style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e6ed',
            borderRadius: 8,
            padding: 'clamp(16px, 4vw, 24px)',
            marginBottom: 16,
        }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                Verify your date of birth
            </div>
            <div style={{ color: '#586473', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
                Confirm your date of birth to access saved payment methods on this invoice.
                Your DOB is checked against your patient record and is never stored on this page.
            </div>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 0.9fr) minmax(0, 1.1fr)',
                gap: 8,
            }}>
                <select
                    ref={monthRef}
                    value={month}
                    onChange={handleMonthChange}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="bday-month"
                    aria-label="Month of birth"
                    style={selectStyle}
                >
                    <option value="">Month</option>
                    {MONTHS.map((name, idx) => (
                        <option key={name} value={idx + 1}>{name}</option>
                    ))}
                </select>
                <select
                    ref={dayRef}
                    value={day}
                    onChange={handleDayChange}
                    disabled={disabled}
                    autoComplete="bday-day"
                    aria-label="Day of birth"
                    style={selectStyle}
                >
                    <option value="">Day</option>
                    {dayOptions.map((d) => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>
                <select
                    ref={yearRef}
                    value={year}
                    onChange={handleYearChange}
                    disabled={disabled}
                    autoComplete="bday-year"
                    aria-label="Year of birth"
                    style={selectStyle}
                >
                    <option value="">Year</option>
                    {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            </div>
            {error && (
                <div style={{ color: '#b3261e', fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                    {error}
                    {attemptsRemaining !== null && lockSecondsLeft === 0 && (
                        <span> {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining.</span>
                    )}
                </div>
            )}
            {lockSecondsLeft > 0 && (
                <div style={{ color: '#b3261e', fontSize: 13, marginTop: 8 }}>
                    Locked. Try again in {Math.floor(lockSecondsLeft / 60)}m {lockSecondsLeft % 60}s.
                </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                    type="submit"
                    disabled={disabled}
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        backgroundColor: disabled ? '#9bb4d4' : '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 15,
                        fontWeight: 500,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                >
                    {submitting ? 'Verifying…' : 'Continue'}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        style={{
                            padding: '10px 14px',
                            backgroundColor: '#fff',
                            color: '#586473',
                            border: '1px solid #c4ccd6',
                            borderRadius: 6,
                            fontSize: 15,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Pay without saving
                    </button>
                )}
            </div>
        </form>
    );
}

const selectStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 16,
    border: '1px solid #c4ccd6',
    borderRadius: 6,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: '#1a2330',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%23586473' d='M0 0l5 6 5-6z'/></svg>\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
};
